import React, { useState } from "react";
import PricingCheckoutModal, { type PricingBilling, type PricingPlan } from "./PricingCheckoutModal";
import {
  ArrowRight,
  BrainCircuit,
  CheckCircle2,
  FileSearch,
  FileText,
  Layers3,
  Monitor,
  Play,
  Smartphone,
  Sparkles,
  Stethoscope,
  Upload,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";

type BillingCycle = "monthly" | "annual";
type Lang = "pt" | "en" | "es";

const PRICES = {
  free:    { BRL: { monthly: "R$0",    annual: "R$0"      }, USD: { monthly: "US$0",  annual: "US$0"    } },
  pro:     { BRL: { monthly: "R$99",   annual: "R$990"    }, USD: { monthly: "US$19", annual: "US$190"  } },
  premium: { BRL: { monthly: "R$199",  annual: "R$1.990"  }, USD: { monthly: "US$39", annual: "US$390"  } },
} as const;

const MONTHLY_EQUIV: Partial<Record<keyof typeof PRICES, Record<Lang, string>>> = {
  pro:     { pt: "equivale a R$82,50/mês",  en: "equiv. R$82.50/mo",  es: "equiv. R$82,50/mes"  },
  premium: { pt: "equivale a R$165,83/mês", en: "equiv. R$165.83/mo", es: "equiv. R$165,83/mes" },
};

const PERIOD: Record<BillingCycle, Record<Lang, string>> = {
  monthly: { pt: "/mês", en: "/mo",  es: "/mes"  },
  annual:  { pt: "/ano", en: "/yr",  es: "/año"  },
};

const TRIAL_TEXT: Record<Lang, { badge: string; title: string; sub: string; cta: string; spots: string; features: string[] }> = {
  pt: {
    badge: "NOVO · APENAS 50 VAGAS",
    title: "Teste Gratuito — 30 dias",
    sub: "Acesso completo sem cartão de crédito. Ativado automaticamente no primeiro login.",
    cta: "Começar Teste Gratuito",
    spots: "Vagas limitadas",
    features: ["50 análises de laudo / mês", "50 planejamentos cirúrgicos / mês", "50 pesquisas em Diretrizes / mês", "Até 100 pacientes"],
  },
  en: {
    badge: "NEW · ONLY 50 SPOTS",
    title: "Free Trial — 30 days",
    sub: "Full access with no credit card. Activated automatically on first login.",
    cta: "Start Free Trial",
    spots: "Limited spots",
    features: ["50 report analyses / month", "50 surgical plans / month", "50 guideline searches / month", "Up to 100 patients"],
  },
  es: {
    badge: "NUEVO · SOLO 50 PLAZAS",
    title: "Prueba Gratuita — 30 días",
    sub: "Acceso completo sin tarjeta de crédito. Activado automáticamente en el primer inicio de sesión.",
    cta: "Comenzar Prueba Gratuita",
    spots: "Plazas limitadas",
    features: ["50 análisis de informes / mes", "50 planificaciones quirúrgicas / mes", "50 búsquedas en Directrices / mes", "Hasta 100 pacientes"],
  },
};

const UI_TEXT: Record<string, Record<Lang, string>> = {
  sectionLabel:    { pt: "Planos",   en: "Plans",   es: "Planes"  },
  sectionTitle:    { pt: "Escolha o plano ideal para sua prática.",    en: "Choose the right plan for your practice.", es: "Elige el plan ideal para tu práctica."    },
  sectionSubtitle: { pt: "Sem contratos longos. Cancele quando quiser.", en: "No long-term contracts. Cancel anytime.",  es: "Sin contratos largos. Cancela cuando quieras." },
  toggleMonthly:   { pt: "Mensal",   en: "Monthly", es: "Mensual" },
  toggleAnnual:    { pt: "Anual",    en: "Annual",  es: "Anual"   },
  toggleSaving:    { pt: "2 meses grátis", en: "2 months free", es: "2 meses gratis" },
  platformBenefit: { pt: "Acesso completo via Web e Mobile", en: "Full access via Web & Mobile", es: "Acceso completo vía Web y Móvil" },
};

const PLANS_DATA = [
  {
    id: "free",
    name: "FREE",
    badge:    { pt: null,            en: null,            es: null            } as Record<Lang, string | null>,
    subtitle: { pt: "Ideal para conhecer a plataforma",   en: "Ideal to get started",              es: "Ideal para comenzar"             },
    description: {
      pt: "Perfeito para residentes e ortopedistas que desejam experimentar o OrthoPrecision.",
      en: "Perfect for residents and orthopedists who want to explore OrthoPrecision.",
      es: "Perfecto para residentes y ortopedistas que desean explorar OrthoPrecision.",
    },
    benefits: {
      pt: ["Até 3 pacientes", "5 análises de laudo por mês", "3 planejamentos cirúrgicos por mês", "Diretrizes básicas", "Exportação PDF simples", "Marca d'água OrthoPrecision"],
      en: ["Up to 3 patients", "5 report analyses per month", "3 surgical plans per month", "Basic guidelines", "Basic PDF export", "OrthoPrecision watermark"],
      es: ["Hasta 3 pacientes", "5 análisis de informes por mes", "3 planificaciones quirúrgicas por mes", "Directrices básicas", "Exportación PDF básica", "Marca de agua OrthoPrecision"],
    },
    cta:      { pt: "Começar Gratuitamente",  en: "Get Started for Free",  es: "Comenzar Gratis"       },
    footnote: { pt: null, en: null, es: null } as Record<Lang, string | null>,
  },
  {
    id: "pro",
    name: "PRO",
    badge:    { pt: "MAIS POPULAR",  en: "MOST POPULAR",  es: "MÁS POPULAR"  } as Record<Lang, string | null>,
    subtitle: { pt: "O essencial para o ortopedista moderno", en: "The essential plan for modern orthopedists", es: "El plan esencial para el ortopedista moderno" },
    description: {
      pt: "Mais velocidade, organização e suporte clínico no dia a dia.",
      en: "More speed, organization, and clinical support in your daily workflow.",
      es: "Más velocidad, organización y soporte clínico en el día a día.",
    },
    benefits: {
      pt: ["Pacientes ilimitados", "30 análises de laudo por mês", "30 planejamentos cirúrgicos por mês", "30 pesquisas em Diretrizes por mês", "30 consultas patológicas por mês", "PDFs premium", "Histórico completo", "Armazenamento seguro", "IA clínica otimizada"],
      en: ["Unlimited patients", "30 report analyses per month", "30 surgical plans per month", "30 guideline searches per month", "30 pathology consults per month", "Premium PDFs", "Full history", "Secure storage", "Optimized clinical AI"],
      es: ["Pacientes ilimitados", "30 análisis de informes por mes", "30 planificaciones quirúrgicas por mes", "30 búsquedas en Directrices por mes", "30 consultas patológicas por mes", "PDFs premium", "Historial completo", "Almacenamiento seguro", "IA clínica optimizada"],
    },
    cta:      { pt: "Assinar Plano Pro",  en: "Subscribe to Pro",   es: "Suscribirse al Pro"   },
    footnote: { pt: null, en: null, es: null } as Record<Lang, string | null>,
  },
  {
    id: "premium",
    name: "PREMIUM",
    badge:    { pt: null, en: null, es: null } as Record<Lang, string | null>,
    subtitle: { pt: "Máxima performance clínica", en: "Maximum clinical performance", es: "Máximo rendimiento clínico" },
    description: {
      pt: "Experiência avançada para profissionais com uso intenso e maior demanda clínica.",
      en: "Advanced experience for professionals with high-volume and demanding clinical workflows.",
      es: "Experiencia avanzada para profesionales con uso intensivo y mayor demanda clínica.",
    },
    benefits: {
      pt: ["Tudo do Pro", "100 análises de laudo por mês", "100 planejamentos cirúrgicos por mês", "100 pesquisas em Diretrizes por mês", "100 consultas patológicas por mês", "Prioridade máxima IA", "Processamento mais rápido", "Evidências científicas automáticas", "Comparação de técnicas cirúrgicas", "Protocolos pós-operatórios completos", "Templates personalizados", "Exportações institucionais premium", "Acesso antecipado a novos módulos", "Suporte prioritário"],
      en: ["Everything in Pro", "100 report analyses per month", "100 surgical plans per month", "100 guideline searches per month", "100 pathology consults per month", "Maximum AI priority", "Faster processing", "Automatic scientific evidence", "Surgical technique comparison", "Complete post-op protocols", "Custom templates", "Premium institutional exports", "Early access to new modules", "Priority support"],
      es: ["Todo lo del Pro", "100 análisis de informes por mes", "100 planificaciones quirúrgicas por mes", "100 búsquedas en Directrices por mes", "100 consultas patológicas por mes", "Prioridad máxima de IA", "Procesamiento más rápido", "Evidencias científicas automáticas", "Comparación de técnicas quirúrgicas", "Protocolos postoperatorios completos", "Plantillas personalizadas", "Exportaciones institucionales premium", "Acceso anticipado a nuevos módulos", "Soporte prioritario"],
    },
    cta:      { pt: "Evoluir para Premium",  en: "Upgrade to Premium",  es: "Mejorar a Premium"   },
    footnote: { pt: null, en: null, es: null } as Record<Lang, string | null>,
  },
];

const LandingPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [checkoutModal, setCheckoutModal] = useState<{
    plan: PricingPlan;
    billing: PricingBilling;
  } | null>(null);
  const [demoStarted, setDemoStarted] = useState(false);

  const lang = language as Lang;
  const currency = language === "pt" ? "BRL" : "USD";

  const plans = PLANS_DATA.map((p) => ({
    id: p.id,
    name: p.name,
    price: PRICES[p.id as keyof typeof PRICES][currency][billingCycle],
    period: PERIOD[billingCycle][lang],
    badge: p.badge[lang],
    subtitle: p.subtitle[lang],
    description: p.description[lang],
    platformBenefit: UI_TEXT.platformBenefit[lang],
    benefits: p.benefits[lang],
    cta: p.cta[lang],
    footnote: p.footnote[lang],
    monthlyEquiv: (billingCycle === "annual" && currency === "BRL")
      ? (MONTHLY_EQUIV[p.id as keyof typeof MONTHLY_EQUIV]?.[lang] ?? null)
      : null,
  }));

  const timelineSteps = [
    {
      time: "00:12",
      title: t('landingStepUploadTitle'),
      description: t('landingStepUploadDescription'),
      icon: Upload,
    },
    {
      time: "00:47",
      title: t('landingStepAiTitle'),
      description: t('landingStepAiDescription'),
      icon: Sparkles,
    },
    {
      time: "01:32",
      title: t('landingStepPlanTitle'),
      description: t('landingStepPlanDescription'),
      icon: Stethoscope,
    },
  ];

  const platformModules = [
    {
      title: t('landingModuleReportTitle'),
      description: t('landingModuleReportDescription'),
      icon: FileSearch,
    },
    {
      title: t('landingModuleAtlasTitle'),
      description: t('landingModuleAtlasDescription'),
      icon: Layers3,
    },
    {
      title: t('landingModulePlanningTitle'),
      description: t('landingModulePlanningDescription'),
      icon: BrainCircuit,
    },
    {
      title: t('landingModuleRecordTitle'),
      description: t('landingModuleRecordDescription'),
      icon: FileText,
    },
  ];

  return (
    <>
    <div className="min-h-screen overflow-hidden bg-slate-950 font-sans text-slate-100">
      {/* NAV */}
      <nav className="sticky top-0 z-20 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-[1360px] items-center justify-between gap-4 px-6 py-4 xl:px-10">
          <div className="flex items-center gap-3">
            <img
              src="/ortho-logo.png"
              alt="OrthoPrecision"
              className="h-9 w-9 object-contain drop-shadow-[0_0_10px_rgba(251,146,60,0.35)]"
            />
            <span className="font-accent text-lg font-semibold tracking-tight text-white">
              OrthoPrecision
            </span>
          </div>

          <div className="hidden items-center gap-8 text-sm text-slate-300 md:flex">
            <a href="#video" className="transition-colors hover:text-orange-300">{t('landingNavVideoLink')}</a>
            <a href="#modulos" className="transition-colors hover:text-orange-300">{t('landingNavProductLink')}</a>
            <a href="#planos" className="transition-colors hover:text-orange-300">{t('landingNavPlansLink')}</a>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <LanguageSelector />
            <a
              href="/app"
              className="hidden items-center justify-center rounded-full border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-orange-500/40 hover:text-white sm:inline-flex"
            >
              {t('landingNavEnter')}
            </a>
            <a
              href="#planos"
              className="inline-flex items-center justify-center rounded-full bg-orange-500 px-4 py-2 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition-all hover:bg-orange-400"
            >
              {t('landingNavTrialCta')}
            </a>
          </div>
        </div>
      </nav>

      {/* HERO VIDEO (full bleed) */}
      <section className="relative min-h-[78vh] w-full overflow-hidden bg-black">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        >
          <source src="/videos/hero-loop.mp4" type="video/mp4" />
        </video>

        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(3,7,14,0.96) 0%, rgba(3,7,14,0.82) 35%, rgba(3,7,14,0.35) 70%, rgba(3,7,14,0.15) 100%), linear-gradient(0deg, rgba(3,4,7,0.7) 0%, rgba(3,4,7,0.32) 38%, rgba(3,4,7,0.05) 65%, rgba(3,4,7,0.22) 100%)",
          }}
        />

        <div className="absolute right-5 top-5 z-10 flex items-center gap-2 font-data text-[11px] uppercase tracking-[0.08em] text-slate-200/80">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-400" />
          {t('landingHeroVideoBadge')}
        </div>

        <div className="relative z-10 flex min-h-[78vh] flex-col justify-end px-6 pb-14 pt-24 sm:px-10 lg:px-[clamp(1.5rem,5vw,3.75rem)]">
          <span className="font-data inline-flex w-fit items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-orange-300 before:h-px before:w-5 before:bg-orange-400 before:content-['']">
            OrthoPrecision · {t('landingHeroTagline')}
          </span>
          <h1 className="font-display mt-4 max-w-[21ch] text-[2.3rem] font-extrabold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-[3.9rem]">
            {t('landingHeroHeadline')}
          </h1>
          <p className="mt-5 max-w-[46ch] text-base leading-relaxed text-slate-200/80 sm:text-lg">
            {t('landingHeroSubtitle')}
          </p>
          <p className="font-accent mt-4 max-w-[32ch] text-base italic text-orange-300">
            {t('landingHeroSlogan')}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#planos"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-6 py-3.5 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition-all hover:bg-orange-400"
            >
              {t('landingStartFree')}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#video"
              className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:border-white/60 hover:bg-white/10"
            >
              {t('landingHeroCtaWatch')}
            </a>
          </div>
        </div>
      </section>

      {/* VALUE BAND */}
      <section className="w-full border-b border-slate-800/80 bg-slate-900/60">
        <div className="mx-auto flex w-full max-w-[1360px] flex-wrap items-center justify-between gap-8 px-6 py-10 xl:px-10">
          <div className="flex flex-wrap gap-10">
            <div className="max-w-[24ch]">
              <h4 className="text-base font-bold text-white">{t('landingValueCol1Title')}</h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{t('landingValueCol1Description')}</p>
            </div>
            <div className="max-w-[24ch]">
              <h4 className="text-base font-bold text-white">{t('landingValueCol2Title')}</h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{t('landingValueCol2Description')}</p>
            </div>
            <div className="max-w-[24ch]">
              <h4 className="text-base font-bold text-white">{t('landingValueCol3Title')}</h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{t('landingValueCol3Description')}</p>
            </div>
          </div>
          <a
            href="#planos"
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition-all hover:bg-orange-400"
          >
            {t('landingValueCta')}
          </a>
        </div>
      </section>

      <main className="mx-auto w-full max-w-[1360px] px-6 xl:px-10">
        {/* VIDEO DEMO */}
        <section id="video" className="border-b border-slate-800/80 py-16 lg:py-20">
          <div className="mb-10 max-w-2xl">
            <span className="font-data inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-orange-300 before:h-px before:w-5 before:bg-orange-400 before:content-['']">
              {t('landingVideoSectionEyebrow')}
            </span>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {t('landingVideoSectionTitle')}
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-400 sm:text-base">
              {t('landingVideoSectionSubtitle')}
            </p>
          </div>

          <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-start">
            <div className="mx-auto w-full max-w-[380px]">
              <div className="relative aspect-9/16 overflow-hidden rounded-[1.8rem] border border-slate-800 bg-black shadow-2xl shadow-black/40">
                <video
                  className="absolute inset-0 h-full w-full object-cover"
                  playsInline
                  preload="metadata"
                  controls={demoStarted}
                >
                  <source src="/videos/product-demo.mp4" type="video/mp4" />
                </video>

                {!demoStarted && (
                  <button
                    type="button"
                    aria-label={t('landingVideoPlayAria')}
                    onClick={(event) => {
                      const container = event.currentTarget.parentElement;
                      const video = container?.querySelector("video");
                      if (video) {
                        video.play();
                      }
                      setDemoStarted(true);
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-black/55 via-black/5 to-black/35 transition-opacity hover:from-black/45"
                  >
                    <span className="font-data absolute left-4 top-4 text-[11px] uppercase tracking-[0.08em] text-slate-100/90">
                      {t('landingVideoDemoCaption')}
                    </span>
                    <span className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-orange-500 text-slate-950 shadow-lg shadow-orange-500/30 transition-transform hover:scale-105">
                      <Play className="h-6 w-6 fill-current pl-0.5" />
                    </span>
                    <span className="font-data absolute bottom-4 right-4 rounded-md bg-black/55 px-2.5 py-1 text-xs tracking-wide text-white">
                      1:35
                    </span>
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col">
              {timelineSteps.map(({ time, title, description, icon: Icon }, index) => (
                <div
                  key={title}
                  className={`flex gap-4 py-[1.15rem] ${index === 0 ? "" : "border-t border-slate-800/80"}`}
                >
                  <span className="font-data w-14 shrink-0 pt-0.5 text-sm text-orange-300">{time}</span>
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <Icon className="h-4 w-4 text-orange-300/80" />
                      <h3 className="text-base font-semibold text-white">{title}</h3>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-400">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MODULES */}
        <section id="modulos" className="border-b border-slate-800/80 py-16 lg:py-20">
          <div className="mb-10 max-w-2xl">
            <span className="font-data inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-orange-300 before:h-px before:w-5 before:bg-orange-400 before:content-['']">
              {t('landingModulesLabel')}
            </span>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {t('landingModulesTitle')}
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {platformModules.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="group rounded-[1.75rem] border border-slate-800 bg-slate-900/50 p-6 transition-all hover:-translate-y-1 hover:border-orange-500/30"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-orange-500/20 bg-orange-500/10 text-orange-300">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-xl font-bold text-white">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-400">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PRICING */}
        <section id="planos" className="border-b border-slate-800/80 py-16 lg:py-20">
          <div className="mb-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="font-data inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-orange-300 before:h-px before:w-5 before:bg-orange-400 before:content-['']">
                {UI_TEXT.sectionLabel[lang]}
              </span>
              <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                {UI_TEXT.sectionTitle[lang]}
              </h2>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <p className="text-sm text-slate-400">
                  {UI_TEXT.sectionSubtitle[lang]}
                </p>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-700/60 bg-slate-800/50 px-3 py-1 text-xs text-slate-400">
                  <Monitor className="h-3 w-3 text-orange-400/70" />
                  <Smartphone className="h-3 w-3 text-orange-400/70" />
                  {t('landingPlatformAvailability')}
                </span>
              </div>
            </div>

            {/* Billing cycle toggle */}
            <div className="flex shrink-0 items-center gap-1 self-start rounded-xl border border-slate-800 bg-slate-900/60 p-1 sm:self-auto">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={[
                  "rounded-lg px-4 py-1.5 text-sm font-semibold transition-all",
                  billingCycle === "monthly"
                    ? "bg-slate-700 text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-200",
                ].join(" ")}
              >
                {UI_TEXT.toggleMonthly[lang]}
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={[
                  "flex items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-semibold transition-all",
                  billingCycle === "annual"
                    ? "bg-slate-700 text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-200",
                ].join(" ")}
              >
                {UI_TEXT.toggleAnnual[lang]}
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-bold text-emerald-400">
                  {UI_TEXT.toggleSaving[lang]}
                </span>
              </button>
            </div>
          </div>

          {/* Trial banner */}
          {(() => {
            const tr = TRIAL_TEXT[lang];
            return (
              <div className="relative mb-8 overflow-hidden rounded-3xl border border-orange-500/30 bg-linear-to-r from-orange-950/40 via-slate-900/80 to-orange-950/30 p-6 sm:p-7">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(251,146,60,0.14),transparent_55%)]" />
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <span className="mb-3 inline-block rounded-full border border-orange-500/30 bg-orange-500/20 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-orange-300">
                      {tr.badge}
                    </span>
                    <h3 className="text-xl font-bold text-white sm:text-2xl">{tr.title}</h3>
                    <p className="mt-1.5 max-w-xl text-sm text-slate-400">{tr.sub}</p>
                    <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                      {tr.features.map((f) => (
                        <li key={f} className="flex items-center gap-1.5 text-xs text-orange-300/80">
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-orange-400" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a
                    href="/app"
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition-all hover:bg-orange-400 hover:shadow-orange-400/25 sm:self-center"
                  >
                    {tr.cta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            );
          })()}

          {/* Cards */}
          <div className="grid gap-6 md:grid-cols-3">
            {plans.map((plan) => {
              const isFree = plan.id === "free";
              const isPro = plan.id === "pro";

              return (
                <div
                  key={plan.id}
                  className={[
                    "relative flex flex-col rounded-[1.75rem] p-7",
                    isFree
                      ? "border border-slate-800 bg-slate-900/50"
                      : isPro
                      ? "border border-orange-500/40 bg-slate-900/70 shadow-[0_0_48px_-12px_rgba(251,146,60,0.22)]"
                      : "border border-amber-500/30 bg-gradient-to-b from-slate-900/80 to-slate-950/80",
                  ].join(" ")}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span
                        className={[
                          "rounded-full px-4 py-1 text-xs font-bold tracking-[0.12em]",
                          isPro
                            ? "bg-orange-500 text-slate-950"
                            : "bg-amber-600 text-white",
                        ].join(" ")}
                      >
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  {/* Plan name + price */}
                  <div>
                    <span
                      className={[
                        "text-xs font-bold uppercase tracking-[0.28em]",
                        isFree ? "text-slate-500" : isPro ? "text-orange-400" : "text-amber-400",
                      ].join(" ")}
                    >
                      {plan.name}
                    </span>
                    <div className="mt-4 flex items-end gap-1">
                      <span className="font-accent text-4xl font-bold tracking-tight text-white">
                        {plan.price}
                      </span>
                      <span className="mb-1.5 text-sm text-slate-400">
                        {plan.period}
                      </span>
                    </div>
                    {plan.monthlyEquiv && (
                      <p className="mt-0.5 text-xs text-slate-400/80">{plan.monthlyEquiv}</p>
                    )}
                    <p
                      className={[
                        "mt-1.5 text-sm",
                        isFree ? "text-slate-500" : isPro ? "text-orange-300/70" : "text-amber-300/70",
                      ].join(" ")}
                    >
                      {plan.subtitle}
                    </p>
                  </div>

                  <p className="mt-4 border-t border-slate-800/70 pt-4 text-sm leading-6 text-slate-400">
                    {plan.description}
                  </p>

                  {/* Benefits */}
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {/* Platform benefit — special pill */}
                    <li className="flex items-center gap-2.5 rounded-lg border border-slate-700/50 bg-slate-800/40 px-3 py-2 text-sm font-medium text-slate-200">
                      <Monitor className="h-3.5 w-3.5 shrink-0 text-orange-400/80" />
                      <Smartphone className="h-3.5 w-3.5 shrink-0 text-orange-400/80" />
                      {plan.platformBenefit}
                    </li>
                    {plan.benefits.map((benefit) => (
                      <li
                        key={benefit}
                        className="flex items-start gap-2.5 text-sm text-slate-300"
                      >
                        <CheckCircle2
                          className={[
                            "mt-0.5 h-4 w-4 shrink-0",
                            isFree ? "text-slate-600" : isPro ? "text-orange-400" : "text-amber-400",
                          ].join(" ")}
                        />
                        {benefit}
                      </li>
                    ))}
                  </ul>

                  {isFree ? (
                    <a
                      href="/app"
                      className="mt-7 inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-all hover:border-slate-500 hover:text-white"
                    >
                      {plan.cta}
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setCheckoutModal({
                          plan: plan.id as PricingPlan,
                          billing: billingCycle as PricingBilling,
                        })
                      }
                      className={[
                        "mt-7 inline-flex w-full items-center justify-center rounded-xl px-5 py-2.5 text-sm font-bold transition-all",
                        isPro
                          ? "bg-orange-500 text-slate-950 shadow-lg shadow-orange-500/20 hover:bg-orange-400"
                          : "bg-amber-600 text-white shadow-lg shadow-amber-500/20 hover:bg-amber-500",
                      ].join(" ")}
                    >
                      {plan.cta}
                    </button>
                  )}

                  {plan.footnote && (
                    <p className="mt-3 text-center text-xs text-slate-500">
                      {plan.footnote}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 lg:py-20">
          <div className="relative overflow-hidden rounded-[2rem] border border-slate-800/80 bg-slate-900/55 p-6 backdrop-blur-xl sm:p-8 lg:p-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(251,146,60,0.14),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(217,119,6,0.12),transparent_38%)]" />
            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <span className="font-data inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-orange-300 before:h-px before:w-5 before:bg-orange-400 before:content-['']">
                  {t('landingFinalLabel')}
                </span>
                <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                  {t('landingFinalTitle')}
                </h2>
                <p className="mt-4 text-base leading-8 text-slate-300">
                  {t('landingFinalSubtitle')}
                </p>
                <p className="font-accent mt-3 text-lg italic text-orange-300">
                  {t('landingHeroSlogan')}
                </p>
              </div>

              <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                <a
                  href="/app"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-500 px-6 py-3.5 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition-all hover:bg-orange-400"
                >
                  {t('landingStartFree')}
                  <ArrowRight className="h-4 w-4" />
                </a>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </section>

        <footer className="flex justify-center pb-10 pt-2">
          <nav className="flex flex-wrap items-center justify-center gap-4 text-xs font-medium text-slate-500">
            <a href="/suporte" className="transition-colors hover:text-orange-300">
              {t('publicFooterSupport')}
            </a>
            <a href="/terms" className="transition-colors hover:text-orange-300">
              {t('publicFooterTerms')}
            </a>
            <a href="/privacy" className="transition-colors hover:text-orange-300">
              {t('publicFooterPrivacy')}
            </a>
          </nav>
        </footer>
      </main>
    </div>

    {checkoutModal && (
      <PricingCheckoutModal
        plan={checkoutModal.plan}
        billing={checkoutModal.billing}
        onClose={() => setCheckoutModal(null)}
      />
    )}
    </>
  );
};

export default LandingPage;
